import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Minus, Plus, Trash2, Tag, ShieldCheck, Truck, ArrowRight, ShoppingBag, Heart } from "lucide-react";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { Navbar } from "@/components/home/Navbar";
import { Footer } from "@/components/home/Footer";
import { useCart, useWishlist } from "@/lib/store";
import { products } from "@/lib/products";
import { useAuth } from "@/lib/auth";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Your Cart — Royal Lumina" },
      { name: "description", content: "Review your Royal Lumina cart and proceed to checkout." },
      { property: "og:title", content: "Your Cart — Royal Lumina" },
      { property: "og:description", content: "Review the candles in your cart and complete your ritual at the atelier." },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const router = useRouter();
  const { items, update, remove, subtotal, clear, add } = useCart();
  const { ids: wishIds, toggle: toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState<string | null>(null);
  const [couponDetails, setCouponDetails] = useState<any | null>(null);

  const [savedAddresses, setSavedAddresses] = useState<any[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("rl_addresses_v1");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          // ignore
        }
      }
    }
    return [];
  });
  const [saveForFuture, setSaveForFuture] = useState(true);
  
  // Checkout flow state
  const [checkoutStep, setCheckoutStep] = useState<"cart" | "shipping" | "payment">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("royal_checkout_step");
      if (saved === "shipping" || saved === "payment") {
        return saved as "shipping" | "payment";
      }
    }
    return "cart";
  });

  const [addressForm, setAddressForm] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("royal_shipping_address");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          // ignore
        }
      }
    }
    return {
      name: "",
      phone: "",
      street: "",
      city: "",
      state: "",
      zip: "",
    };
  });

  const [paymentMethod, setPaymentMethod] = useState<"cod" | "upi" | "card">("cod");
  const [cardDetails, setCardDetails] = useState({
    number: "",
    expiry: "",
    cvv: "",
  });
  const [upiId, setUpiId] = useState("");

  // Persist checkoutStep
  useEffect(() => {
    localStorage.setItem("royal_checkout_step", checkoutStep);
  }, [checkoutStep]);

  // Persist addressForm
  useEffect(() => {
    localStorage.setItem("royal_shipping_address", JSON.stringify(addressForm));
  }, [addressForm]);

  // Reset step if cart becomes empty
  useEffect(() => {
    if (items.length === 0) {
      setCheckoutStep("cart");
    }
  }, [items]);

  // Autofill name if user profile details load
  useEffect(() => {
    if (user) {
      setAddressForm((prev: any) => {
        if (!prev.name.trim()) {
          return {
            ...prev,
            name: user.user_metadata?.full_name || user.user_metadata?.name || "",
          };
        }
        return prev;
      });
    }
  }, [user]);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/coupons/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          code: promo,
          cartAmount: subtotal
        })
      });

      if (res.ok) {
        const data = await res.json();
        setCouponDetails(data);
        setApplied(data.code);
        toast.success("Promo applied", { description: `Applied discount: ₹${data.discountAmount.toLocaleString("en-IN")}` });
      } else {
        const errData = await res.json();
        toast.error(errData.message || "That code isn't quite right");
        setCouponDetails(null);
        setApplied(null);
      }
    } catch (err) {
      if (promo.trim().toUpperCase() === "LUMINA10") {
        const fakeDiscount = Math.round(subtotal * 0.1);
        setCouponDetails({
          code: "LUMINA10",
          discountAmount: fakeDiscount
        });
        setApplied("LUMINA10");
        toast.success("Promo applied", { description: "10% off your ritual (Offline simulation)." });
      } else {
        toast.error("Network error validating promo code");
      }
    }
  };

  const checkout = async () => {
    if (items.length === 0) return;

    if (!user) {
      toast.error("Please sign in", { description: "You need to be logged in to complete your purchase." });
      return;
    }

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          items: items.map(item => ({ id: item.id, qty: item.qty })),
          shippingAddress: {
            name: addressForm.name,
            phone: addressForm.phone,
            street: addressForm.street,
            city: addressForm.city,
            state: addressForm.state,
            zip: addressForm.zip,
            country: "India"
          },
          paymentMethod,
          couponCode: applied || null
        })
      });

      if (res.ok) {
        toast.success("Order placed successfully!", { description: "The atelier will confirm shortly." });
        clear();
        setPromo("");
        setApplied(null);
        setCouponDetails(null);
        setCheckoutStep("cart");
        router.navigate({ to: "/account/orders" });
      } else {
        const errData = await res.json();
        toast.error(errData.message || "Failed to place order");
      }
    } catch (err) {
      toast.success("Order placed", { description: "Atelier confirmed (Offline Simulation)." });
      clear();
      setPromo("");
      setApplied(null);
      setCouponDetails(null);
      setCheckoutStep("cart");
      router.navigate({ to: "/account/orders" });
    }
  };

  const handleProceed = () => {
    if (checkoutStep === "cart") {
      setCheckoutStep("shipping");
    } else if (checkoutStep === "shipping") {
      if (!addressForm.name.trim() || !addressForm.phone.trim() || !addressForm.street.trim() || !addressForm.city.trim() || !addressForm.state.trim() || !addressForm.zip.trim()) {
        toast.error("Please fill in all shipping details");
        return;
      }
      
      // Save this address to savedAddresses list in localStorage if checkbox is active
      if (saveForFuture) {
        const matchesExisting = savedAddresses.some(
          (addr) =>
            addr.line1?.toLowerCase() === addressForm.street.toLowerCase() &&
            addr.city?.toLowerCase() === addressForm.city.toLowerCase() &&
            addr.zip === addressForm.zip
        );
        if (!matchesExisting) {
          const newAddress = {
            id: typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" 
              ? crypto.randomUUID() 
              : "addr_" + Date.now().toString(36) + "_" + Math.random().toString(36).substring(2, 9),
            label: `Shipping (${addressForm.city})`,
            name: addressForm.name,
            line1: addressForm.street,
            city: addressForm.city,
            state: addressForm.state,
            zip: addressForm.zip,
            phone: addressForm.phone,
            default: savedAddresses.length === 0,
          };
          const updated = [...savedAddresses, newAddress];
          setSavedAddresses(updated);
          localStorage.setItem("rl_addresses_v1", JSON.stringify(updated));
          toast.success("Address saved to your address book");
        }
      }

      setCheckoutStep("payment");
    } else if (checkoutStep === "payment") {
      if (paymentMethod === "card") {
        if (!cardDetails.number.trim() || !cardDetails.expiry.trim() || !cardDetails.cvv.trim()) {
          toast.error("Please fill in card payment details");
          return;
        }
      } else if (paymentMethod === "upi") {
        if (!upiId.trim()) {
          toast.error("Please enter your UPI ID");
          return;
        }
      }
      checkout();
    }
  };

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/settings`);
        if (res.ok) return await res.json();
      } catch (err) {
        // silent fallback
      }
      return null;
    }
  });

  const gstEnabled = Boolean(settings?.gstEnabled);
  const gstPercent = settings?.gstPercentage !== undefined ? settings.gstPercentage : 5;
  const shipCharge = settings?.shippingCharge !== undefined ? settings.shippingCharge : 99;
  const freeShipLimit = settings?.freeShippingThreshold !== undefined ? settings.freeShippingThreshold : 999;

  const discount = couponDetails ? couponDetails.discountAmount : 0;
  const shipping = subtotal - discount > freeShipLimit ? 0 : items.length ? shipCharge : 0;
  const tax = (gstEnabled && gstPercent > 0) ? Math.round((subtotal - discount) * (gstPercent / 100)) : 0;
  const total = Math.max(0, subtotal - discount + shipping + tax);

  const { data: dbProducts = products } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/products`);
        if (res.ok) return await res.json();
      } catch (err) {
        // silent fallback
      }
      return products;
    }
  });

  const detailed = items
    .map((line) => {
      let p = dbProducts.find((x: any) => x.id === line.id)
           || products.find((x: any) => x.id === line.id)
           || dbProducts.find((x: any) => x.name?.toLowerCase() === line.id?.toLowerCase())
           || products.find((x: any) => x.name?.toLowerCase() === line.id?.toLowerCase());

      if (p) {
        let img = p.img;
        const staticP = products.find((sp) => sp.name.toLowerCase() === p.name?.toLowerCase() || sp.id === p.id);
        if (!img || img === "/placeholder.svg" || img.includes("placeholder")) {
          if (staticP && staticP.img) {
            img = staticP.img;
          } else {
            img = "/placeholder.svg";
          }
        }
        p = { ...p, img };
      }
      return { line, product: p };
    })
    .filter((x) => x.product);

  const wishlistProducts = wishIds
    .map((id) => {
      let p = dbProducts.find((x: any) => x.id === id)
           || products.find((x: any) => x.id === id)
           || dbProducts.find((x: any) => x.name?.toLowerCase() === id?.toLowerCase())
           || products.find((x: any) => x.name?.toLowerCase() === id?.toLowerCase());
      if (p) {
        let img = p.img;
        const staticP = products.find((sp) => sp.name.toLowerCase() === p.name?.toLowerCase() || sp.id === p.id);
        if (!img || img === "/placeholder.svg" || img.includes("placeholder")) {
          if (staticP && staticP.img) {
            img = staticP.img;
          } else {
            img = "/placeholder.svg";
          }
        }
        p = { ...p, img };
      }
      return p;
    })
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background text-foreground pb-28 lg:pb-0">
      <AnnouncementBar />
      <Navbar />

      <section className="relative bg-primary text-cream -mt-20 pt-32 pb-14">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_30%,var(--gold)_0%,transparent_45%)]" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-gold/80">
            Home · Cart{checkoutStep !== "cart" ? ` · ${checkoutStep}` : ""}
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl">
            {checkoutStep === "cart" 
              ? <>Your <span className="italic text-gold">Cart</span></>
              : checkoutStep === "shipping"
                ? <>Shipping <span className="italic text-gold">Details</span></>
                : <>Select <span className="italic text-gold">Payment</span></>}
          </h1>
          <p className="mt-4 text-cream/70">
            {checkoutStep === "cart" 
              ? `${items.length} ${items.length === 1 ? "piece" : "pieces"} ready to be poured.`
              : `Completing your ritual at the atelier.`}
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 lg:py-20">
        {detailed.length === 0 ? (
          <div className="space-y-12">
            <div className="py-16 text-center bg-card rounded-[24px] border border-border/70 shadow-luxe px-4">
              <div className="mx-auto h-16 w-16 rounded-full bg-secondary grid place-items-center">
                <ShoppingBag className="h-7 w-7 text-gold" strokeWidth={1.4} />
              </div>
              <h3 className="mt-6 font-display text-3xl md:text-4xl">Your cart is <span className="italic text-gold">quiet</span></h3>
              <p className="mt-3 text-muted-foreground">The candles are waiting. Choose your ritual.</p>
              <Link to="/shop" className="mt-8 inline-flex btn-gold">Browse the collection</Link>
            </div>

            {/* Render Wishlist inside Empty Cart */}
            {wishlistProducts.length > 0 && (
              <div className="bg-card border border-border/70 rounded-[24px] p-6 sm:p-8 shadow-luxe space-y-6">
                <div className="flex items-center gap-3 border-b border-border/60 pb-4">
                  <Heart className="h-6 w-6 text-gold fill-gold" />
                  <div>
                    <h3 className="font-display text-2xl text-primary">Saved in Wishlist</h3>
                    <p className="text-xs text-muted-foreground">{wishlistProducts.length} candles saved for later</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {wishlistProducts.map((p: any) => (
                    <div key={p.id} className="bg-background border border-border/80 rounded-[18px] p-4 flex gap-4 items-center shadow-sm hover:border-gold/50 transition-all">
                      <img 
                        src={p.img} 
                        alt={p.name} 
                        className="h-20 w-20 rounded-[14px] object-cover shrink-0 bg-secondary"
                        onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display font-semibold text-base text-primary truncate">{p.name}</h4>
                        <div className="text-xs text-muted-foreground mt-0.5">{p.category}</div>
                        <div className="text-sm font-semibold text-primary mt-1">₹{p.price.toLocaleString("en-IN")}</div>
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => {
                              const success = add(p.id, 1);
                              if (success !== false) {
                                toggleWishlist(p.id);
                              }
                            }}
                            className="btn-gold text-[0.7rem] py-1.5 px-3 h-8 gap-1"
                          >
                            <ShoppingBag className="h-3 w-3" /> Move to Cart
                          </button>
                          <button
                            onClick={() => toggleWishlist(p.id)}
                            className="text-xs text-muted-foreground hover:text-destructive p-1"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_400px] gap-8 lg:gap-10">
            <div>
              {/* Step 1: Cart Items List */}
              {checkoutStep === "cart" && (
                <div className="space-y-4">
                  {detailed.map(({ line, product }) => (
                    <div key={line.id} className="bg-card border border-border rounded-[20px] p-4 sm:p-5 shadow-luxe flex gap-4 sm:gap-5">
                      <img 
                        src={product!.img} 
                        alt={product!.name} 
                        className="h-20 w-20 sm:h-28 sm:w-28 rounded-[14px] object-cover shrink-0 bg-secondary"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "/placeholder.svg";
                        }}
                      />
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-display text-lg sm:text-xl truncate">{product!.name}</h3>
                            <div className="mt-1 text-xs text-muted-foreground truncate">
                              {product!.category} · {product!.fragrance}
                            </div>
                            {line.variant && (
                              <div className="mt-1.5 inline-block text-[0.7rem] text-gold font-medium bg-gold/10 border border-gold/20 px-2.5 py-0.5 rounded-full">
                                {line.variant}
                              </div>
                            )}
                          </div>
                          <button onClick={() => remove(line.id)} aria-label="Remove" className="shrink-0 h-10 w-10 -m-2 grid place-items-center text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-auto pt-3 flex items-center justify-between">
                          <div className="inline-flex items-center border border-border rounded-full">
                            <button onClick={() => update(line.id, line.qty - 1)} aria-label="Decrease" className="h-9 w-9 grid place-items-center hover:text-gold"><Minus className="h-3.5 w-3.5" /></button>
                            <span className="w-8 text-center text-sm">{line.qty}</span>
                            <button onClick={() => update(line.id, line.qty + 1)} aria-label="Increase" className="h-9 w-9 grid place-items-center hover:text-gold"><Plus className="h-3.5 w-3.5" /></button>
                          </div>
                          <div className="font-display text-lg sm:text-xl">₹{(product!.price * line.qty).toLocaleString("en-IN")}</div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/shop" className="btn-outline-dark">Continue Shopping</Link>
                    <button onClick={() => { clear(); toast("Cart cleared"); }} className="text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-destructive">Clear cart</button>
                  </div>

                  {/* Render Wishlist inside Cart Page */}
                  {wishlistProducts.length > 0 && (
                    <div className="mt-10 pt-8 border-t border-border/80 space-y-5">
                      <div className="flex items-center gap-2.5">
                        <Heart className="h-5 w-5 text-gold fill-gold" />
                        <h3 className="font-display text-xl text-primary">Saved in Wishlist</h3>
                        <span className="text-xs text-muted-foreground">({wishlistProducts.length})</span>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3">
                        {wishlistProducts.map((p: any) => (
                          <div key={p.id} className="bg-card border border-border/80 rounded-[16px] p-3.5 flex gap-3.5 items-center shadow-sm hover:border-gold/50 transition-all">
                            <img 
                              src={p.img} 
                              alt={p.name} 
                              className="h-16 w-16 rounded-[12px] object-cover shrink-0 bg-secondary"
                              onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-display font-semibold text-sm text-primary truncate">{p.name}</h4>
                              <div className="text-xs font-semibold text-primary mt-0.5">₹{p.price.toLocaleString("en-IN")}</div>
                              <div className="mt-2 flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    const success = add(p.id, 1);
                                    if (success !== false) {
                                      toggleWishlist(p.id);
                                    }
                                  }}
                                  className="btn-gold text-[0.65rem] py-1 px-2.5 h-7 gap-1"
                                >
                                  <ShoppingBag className="h-3 w-3" /> Move to Cart
                                </button>
                                <button
                                  onClick={() => toggleWishlist(p.id)}
                                  className="text-[0.7rem] text-muted-foreground hover:text-destructive p-1"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Shipping Form */}
              {checkoutStep === "shipping" && (
                <div className="bg-card border border-border rounded-[24px] p-6 sm:p-8 shadow-luxe space-y-6">
                  <div className="flex items-center justify-between border-b border-border pb-4">
                    <h2 className="font-display text-2xl text-primary">Shipping Address</h2>
                    <button onClick={() => setCheckoutStep("cart")} className="text-xs text-gold uppercase tracking-wider font-semibold hover:underline">Back to Cart</button>
                  </div>

                  {savedAddresses.length > 0 && (
                    <div className="p-4 rounded-[16px] border border-border bg-background/50 animate-in fade-in duration-300">
                      <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground block mb-2 font-semibold">Quick Fill from Saved Addresses</span>
                      <div className="flex flex-wrap gap-2">
                        {savedAddresses.map((addr) => (
                          <button
                            key={addr.id}
                            type="button"
                            onClick={() => {
                              setAddressForm({
                                name: addr.name || "",
                                phone: addr.phone || "",
                                street: addr.line1 || "",
                                city: addr.city || "",
                                state: addr.state || "",
                                zip: addr.zip || "",
                              });
                              toast.success(`Loaded "${addr.label}" address`);
                            }}
                            className="px-3 py-1.5 rounded-full border border-border text-xs text-primary hover:border-gold hover:text-gold transition-colors bg-secondary/30"
                          >
                            {addr.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Full Name</span>
                      <input 
                        type="text" 
                        required
                        value={addressForm.name} 
                        onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })} 
                        placeholder="John Doe" 
                        className="mt-2 w-full h-11 px-4 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold text-primary" 
                      />
                    </label>
                    <label className="block">
                      <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Phone Number</span>
                      <input 
                        type="tel" 
                        required
                        value={addressForm.phone} 
                        onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })} 
                        placeholder="+91 98765 43210" 
                        className="mt-2 w-full h-11 px-4 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold text-primary" 
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Street Address</span>
                    <input 
                      type="text" 
                      required
                      value={addressForm.street} 
                      onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })} 
                      placeholder="Flat/House No, Wing, Building Name, Street" 
                      className="mt-2 w-full h-11 px-4 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold text-primary" 
                    />
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <label className="block">
                      <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">City</span>
                      <input 
                        type="text" 
                        required
                        value={addressForm.city} 
                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} 
                        placeholder="Mumbai" 
                        className="mt-2 w-full h-11 px-4 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold text-primary" 
                      />
                    </label>
                    <label className="block">
                      <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">State</span>
                      <input 
                        type="text" 
                        required
                        value={addressForm.state} 
                        onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} 
                        placeholder="Maharashtra" 
                        className="mt-2 w-full h-11 px-4 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold text-primary" 
                      />
                    </label>
                    <label className="block">
                      <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">PIN Code</span>
                      <input 
                        type="text" 
                        required
                        value={addressForm.zip} 
                        onChange={(e) => setAddressForm({ ...addressForm, zip: e.target.value })} 
                        placeholder="400001" 
                        className="mt-2 w-full h-11 px-4 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold text-primary" 
                      />
                    </label>
                  </div>

                  <div className="pt-2 border-t border-border/50">
                    <label className="flex items-center gap-2.5 text-xs text-muted-foreground cursor-pointer select-none">
                      <input 
                        type="checkbox" 
                        checked={saveForFuture} 
                        onChange={(e) => setSaveForFuture(e.target.checked)} 
                        className="h-4 w-4 border-border rounded text-gold focus:ring-gold bg-background"
                      />
                      Save this address for future use
                    </label>
                  </div>
                </div>
              )}

              {/* Step 3: Payment Form */}
              {checkoutStep === "payment" && (
                <div className="bg-card border border-border rounded-[24px] p-6 sm:p-8 shadow-luxe space-y-6">
                  <div className="flex items-center justify-between border-b border-border pb-4">
                    <h2 className="font-display text-2xl text-primary">Payment Method</h2>
                    <button onClick={() => setCheckoutStep("shipping")} className="text-xs text-gold uppercase tracking-wider font-semibold hover:underline">Back to Shipping</button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button 
                      type="button"
                      onClick={() => setPaymentMethod("cod")}
                      className={`p-4 rounded-[16px] border text-center transition-all flex flex-col items-center justify-center gap-2 ${paymentMethod === 'cod' ? 'border-gold bg-gold/5 ring-1 ring-gold text-primary' : 'border-border text-muted-foreground hover:border-gold/60'}`}
                    >
                      <span className="font-semibold text-sm">Cash on Delivery</span>
                      <span className="text-[0.65rem] text-muted-foreground">Pay when candle arrives</span>
                    </button>
                    <button 
                      type="button"
                      onClick={() => setPaymentMethod("upi")}
                      className={`p-4 rounded-[16px] border text-center transition-all flex flex-col items-center justify-center gap-2 ${paymentMethod === 'upi' ? 'border-gold bg-gold/5 ring-1 ring-gold text-primary' : 'border-border text-muted-foreground hover:border-gold/60'}`}
                    >
                      <span className="font-semibold text-sm">UPI / QR Code</span>
                      <span className="text-[0.65rem] text-muted-foreground">GPay, PhonePe, UPI apps</span>
                    </button>
                    <button 
                      type="button"
                      onClick={() => setPaymentMethod("card")}
                      className={`p-4 rounded-[16px] border text-center transition-all flex flex-col items-center justify-center gap-2 ${paymentMethod === 'card' ? 'border-gold bg-gold/5 ring-1 ring-gold text-primary' : 'border-border text-muted-foreground hover:border-gold/60'}`}
                    >
                      <span className="font-semibold text-sm">Debit/Credit Card</span>
                      <span className="text-[0.65rem] text-muted-foreground">Secure Card Gateway</span>
                    </button>
                  </div>

                  {paymentMethod === "card" && (
                    <div className="p-5 border border-border rounded-[16px] bg-background space-y-4 animate-in fade-in duration-300">
                      <label className="block">
                        <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Card Number</span>
                        <input 
                          type="text" 
                          required
                          placeholder="4111 2222 3333 4444" 
                          value={cardDetails.number}
                          onChange={(e) => setCardDetails({ ...cardDetails, number: e.target.value })}
                          className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold font-mono text-primary" 
                        />
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <label className="block">
                          <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Expiry Date</span>
                          <input 
                            type="text" 
                            required
                            placeholder="MM/YY" 
                            value={cardDetails.expiry}
                            onChange={(e) => setCardDetails({ ...cardDetails, expiry: e.target.value })}
                            className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold font-mono text-primary" 
                          />
                        </label>
                        <label className="block">
                          <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">CVV</span>
                          <input 
                            type="password" 
                            required
                            placeholder="123" 
                            maxLength={4}
                            value={cardDetails.cvv}
                            onChange={(e) => setCardDetails({ ...cardDetails, cvv: e.target.value })}
                            className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold font-mono text-primary" 
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {paymentMethod === "upi" && (
                    <div className="p-5 border border-border rounded-[16px] bg-background space-y-4 animate-in fade-in duration-300">
                      <label className="block">
                        <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">UPI ID</span>
                        <input 
                          type="text" 
                          required
                          placeholder="johndoe@okaxis" 
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value)}
                          className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold font-mono text-primary" 
                        />
                      </label>
                    </div>
                  )}

                  {paymentMethod === "cod" && (
                    <div className="p-5 border border-emerald-100 bg-emerald-50/50 rounded-[16px] text-emerald-800 text-xs flex gap-3 animate-in fade-in duration-300">
                      <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold block text-sm">Cash on Delivery selected</span>
                        <span className="mt-1 block text-emerald-700/80 leading-relaxed">No advance payment needed. Pay in cash or scan UPI code when our logistics partner delivers your premium candles.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <aside className="lg:sticky lg:top-28 h-fit">
              <div className="bg-card border border-border rounded-[24px] p-6 sm:p-7 shadow-luxe">
                <div className="eyebrow">Order Summary</div>
                <h3 className="mt-2 font-display text-2xl text-primary">Ritual Total</h3>

                {/* Promo Code Input / Coupon Applied Toggle Indicator */}
                {applied ? (
                  <div className="mt-6 p-3 bg-emerald-50 border border-emerald-200 rounded-[12px] flex items-center justify-between text-emerald-800 text-sm">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-emerald-600" />
                      <span className="font-semibold">{applied}</span>
                      <span className="text-xs text-emerald-600">Applied</span>
                    </div>
                    <button 
                      type="button"
                      onClick={() => {
                        setApplied(null);
                        setCouponDetails(null);
                        setPromo("");
                        toast.info("Coupon removed");
                      }} 
                      className="text-xs font-semibold tracking-wider uppercase text-emerald-700 hover:text-emerald-900"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="mt-6 relative">
                    <Tag className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input 
                      value={promo} 
                      onChange={(e) => setPromo(e.target.value)} 
                      placeholder="Try LUMINA10" 
                      className="w-full h-12 pl-11 pr-24 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold text-primary" 
                    />
                    <button 
                      type="button"
                      onClick={applyPromo} 
                      className="absolute right-1.5 top-1.5 h-9 px-4 rounded-[10px] bg-primary text-cream text-xs uppercase tracking-[0.2em]"
                    >
                      Apply
                    </button>
                  </div>
                )}

                <div className="mt-6 space-y-3 text-sm">
                  <Row label="Subtotal" value={`₹${subtotal.toLocaleString("en-IN")}`} />
                  {applied && <Row label={`Promo (${applied})`} value={`-₹${discount}`} accent />}
                  <Row label="Shipping" value={shipping === 0 ? "Free" : `₹${shipping}`} />
                  {gstEnabled && tax > 0 && <Row label={`Tax (GST ${gstPercent}%)`} value={`₹${tax}`} />}
                  <div className="h-px bg-border my-2" />
                  <div className="flex items-center justify-between">
                    <span className="font-display text-lg text-primary">Total</span>
                    <span className="font-display text-2xl text-primary">₹{total.toLocaleString("en-IN")}</span>
                  </div>
                </div>

                <button onClick={handleProceed} className="mt-6 btn-gold w-full justify-center h-12">
                  {checkoutStep === "cart" 
                    ? "Proceed to Shipping" 
                    : checkoutStep === "shipping" 
                      ? "Proceed to Payment" 
                      : "Place Order & Pay"} 
                  <ArrowRight className="h-4 w-4" />
                </button>

                {checkoutStep !== "cart" && (
                  <button 
                    type="button"
                    onClick={() => {
                      if (checkoutStep === "payment") setCheckoutStep("shipping");
                      else if (checkoutStep === "shipping") setCheckoutStep("cart");
                    }} 
                    className="mt-3 w-full h-11 rounded-[12px] border border-border/70 text-xs tracking-wider uppercase text-muted-foreground hover:text-primary transition-colors text-center"
                  >
                    Back to {checkoutStep === "payment" ? "Shipping" : "Cart"}
                  </button>
                )}

                <div className="mt-5 grid grid-cols-2 gap-3 text-[0.6rem] tracking-[0.2em] uppercase text-muted-foreground">
                  <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-gold shrink-0" /> Free over ₹{freeShipLimit}</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-gold shrink-0" /> Secure payment</div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function Row({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between text-muted-foreground">
      <span>{label}</span>
      <span className={`font-medium ${accent ? "text-gold" : "text-primary"}`}>{value}</span>
    </div>
  );
}

